<template>
  <div class="space-y-4">

    <div v-if="!radiologyTests.length" class="text-sm text-gray-400 py-4">
      No radiology tests recorded for this visit.
    </div>

    <div v-for="test in radiologyTests" :key="test.id"
      class="border border-gray-200 rounded-lg overflow-hidden">

      <!-- Test header -->
      <div class="flex items-center justify-between px-4 py-3 bg-gray-50">
        <div>
          <p class="font-medium text-gray-800 text-sm">{{ test.imaging_type?.name || '—' }}</p>
          <p v-if="test.body_part" class="text-xs text-gray-500 mt-0.5">Body part: {{ test.body_part }}</p>
        </div>
        <div class="flex items-center gap-3">
          <span v-if="test.test_date" class="text-xs text-gray-500">{{ formatDate(test.test_date) }}</span>
          <span v-if="test.queue_priority"
            class="text-xs px-2 py-0.5 rounded-full bg-yellow-50 text-yellow-700 border border-yellow-200">
            Priority: {{ test.queue_priority }}
          </span>
          <span class="text-xs px-2 py-0.5 rounded-full border" :class="statusStyle(test.status?.name)">
            {{ test.status?.name || 'Unknown' }}
          </span>
        </div>
      </div>

      <!-- Results + edit -->
      <div class="px-4 py-3">

        <!-- Viewing existing results (versioned) -->
        <div v-if="editingTestId !== test.id">
          <div v-if="test.radiology_results && test.radiology_results.length" class="space-y-2">
            <div
              v-for="(result, index) in [...test.radiology_results].reverse()"
              :key="result.id || index"
              class="border border-gray-100 rounded p-2 bg-gray-50"
            >
              <div class="flex items-center justify-between mb-2">
                <span class="text-xs font-semibold text-blue-600">
                  {{ index === 0 ? 'Latest Entry' : `Amendment ${test.radiology_results.length - 1 - index}` }}
                </span>
                <span class="text-xs text-gray-400">
                  {{ result.created_at ? new Date(result.created_at).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '' }}
                </span>
              </div>
              <div v-if="result.findings" class="mb-1">
                <p class="text-xs font-semibold text-gray-500 uppercase">Findings</p>
                <p class="text-sm text-gray-800 whitespace-pre-line">{{ result.findings }}</p>
              </div>
              <div v-if="result.recommendation" class="mb-1">
                <p class="text-xs font-semibold text-gray-500 uppercase">Recommendation</p>
                <p class="text-sm text-gray-800 whitespace-pre-line">{{ result.recommendation }}</p>
              </div>
              <div v-if="result.conclusion" class="mb-1">
                <p class="text-xs font-semibold text-gray-500 uppercase">Conclusion</p>
                <p class="text-sm text-gray-800 whitespace-pre-line">{{ result.conclusion }}</p>
              </div>
              <div v-if="result.url && result.url.length" class="flex flex-wrap gap-2 mt-2">
                <a v-for="(file, i) in result.url" :key="i" :href="fileHref(file)"
                  target="_blank" rel="noopener noreferrer" class="block">
                  <img v-if="isImageFile(file)" :src="fileHref(file)" :alt="fileName(file) || `Image ${i + 1}`"
                    class="h-20 w-20 object-cover rounded border border-gray-200 hover:opacity-80" />
                  <span v-else class="text-xs text-blue-600 underline">{{ fileName(file) || `Attachment ${i + 1}` }}</span>
                </a>
              </div>
            </div>
          </div>
          <p v-else class="text-xs text-gray-400">Results pending.</p>

          <button @click="startEditResult(test)"
            class="mt-3 text-xs px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-600 hover:bg-gray-50">
            {{ test.radiology_results?.length ? 'Add Amendment' : 'Add Results' }}
          </button>
        </div>

        <!-- Edit form -->
        <div v-else class="space-y-3">
          <div>
            <label class="text-xs font-semibold text-gray-500 uppercase">Findings</label>
            <textarea v-model="resultForm.findings" rows="4"
              class="mt-1 block w-full rounded border-gray-300 bg-gray-50 text-sm p-2"
              placeholder="Describe the imaging findings..." />
          </div>
          <div>
            <label class="text-xs font-semibold text-gray-500 uppercase">Recommendation</label>
            <textarea v-model="resultForm.recommendation" rows="2"
              class="mt-1 block w-full rounded border-gray-300 bg-gray-50 text-sm p-2"
              placeholder="Clinical recommendation..." />
          </div>
          <div>
            <label class="text-xs font-semibold text-gray-500 uppercase">Conclusion</label>
            <textarea v-model="resultForm.conclusion" rows="2"
              class="mt-1 block w-full rounded border-gray-300 bg-gray-50 text-sm p-2"
              placeholder="Conclusion..." />
          </div>
          <div class="flex gap-2">
            <button @click="cancelEditResult"
              class="text-xs px-3 py-1.5 rounded border border-gray-300 bg-white text-gray-600">
              Cancel
            </button>
            <button @click="saveResult(test.id)" :disabled="savingResultId === test.id"
              class="text-xs px-3 py-1.5 rounded bg-custom-blue text-white hover:opacity-90 disabled:opacity-50">
              {{ savingResultId === test.id ? 'Saving...' : 'Save Results' }}
            </button>
          </div>
        </div>
      </div>

      <p v-if="test.notes" class="px-4 pb-3 text-xs text-gray-500 italic">Notes: {{ test.notes }}</p>
    </div>

  </div>
</template>

<script>
import apiClient from '@/services/api'
import dayjs from 'dayjs'

export default {
  name: 'RadiologyStageView',

  props: {
    detail: { type: Object, required: true },
  },

  emits: ['detail-updated'],

  data() {
    return {
      editingTestId:  null,
      savingResultId: null,
      resultForm:     { findings: '', recommendation: '', conclusion: '' },
    }
  },

  computed: {
    radiologyTests() { return this.detail.radiology_tests || [] },
  },

  methods: {
    startEditResult(test) {
      // Always open a blank form — each save creates a new version, not an overwrite.
      this.resultForm    = { findings: '', recommendation: '', conclusion: '' }
      this.editingTestId = test.id
    },

    cancelEditResult() {
      this.editingTestId = null
    },

    async saveResult(radiologyTestId) {
      this.savingResultId = radiologyTestId
      try {
        const response = await apiClient.upsertRadiologyResult(radiologyTestId, this.resultForm)
        if (response.success) {
          this.$emit('detail-updated')
          this.editingTestId = null
          this.$toast.success('Radiology results saved')
        } else {
          this.$toast.error(response.message || 'Failed to save results')
        }
      } catch {
        this.$toast.error('Failed to save results')
      } finally {
        this.savingResultId = null
      }
    },

    statusStyle(statusName) {
      const s = (statusName || '').toLowerCase()
      if (s.includes('complet') || s.includes('done') || s.includes('publish')) return 'bg-green-50 text-green-700 border-green-200'
      if (s.includes('pending') || s.includes('request')) return 'bg-yellow-50 text-yellow-700 border-yellow-200'
      if (s.includes('reject') || s.includes('cancel')) return 'bg-red-50 text-red-700 border-red-200'
      return 'bg-gray-50 text-gray-600 border-gray-200'
    },

    formatDate(date) {
      return dayjs(date).format('DD MMM YYYY')
    },

    // `result.url` entries come from the backend as `{ url, filename }` objects,
    // but older records may still hold plain strings — support both.
    fileHref(file) {
      return typeof file === 'string' ? file : (file?.url || '')
    },

    fileName(file) {
      return typeof file === 'string' ? '' : (file?.filename || '')
    },

    isImageFile(file) {
      const name = this.fileName(file) || this.fileHref(file)
      return /\.(jpe?g|png|gif|webp|bmp|svg)$/i.test(name)
    },
  },
}
</script>
